'use strict';

const libPictProvider = require('pict-provider');

const libCatalog = require('../config-editor/Config-Namespace-Catalog.js');
const libClone = require('../config-editor/Config-Clone.js');

// Scopes the clone exposes for source (all readable) and target (gated by canWriteScope).
const SCOPES = [ 'Global', 'Customer', 'Project', 'User' ];

/**
 * ConfigCloneProvider — the data layer for cloning a namespace's configuration from one context to
 * another. Reads the source's effective config per NamespaceValue, then writes the selected values at
 * the target scope/context (the Configuration service diffs each Put vs the target's parent). Delegates
 * REST + permission + scope-labelling to the ConfigViewer provider to avoid duplication.
 */
class ConfigCloneProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	onAfterInitialize()
	{
		this._ensureState();
		return super.onAfterInitialize();
	}

	get cv()
	{
		return this.pict.providers.ConfigViewer;
	}

	_ensureState()
	{
		if (!this.pict.AppData.ConfigClone)
		{
			this.pict.AppData.ConfigClone =
			{
				NamespaceKey: (libCatalog.CONFIG_NAMESPACE_CATALOG[0] || {}).NamespaceKey || 'MasterListCatalog',
				SourceScope: 'Customer',
				SourceID: 0,
				TargetScope: 'User',
				TargetID: 0,
				Values: [],                 // [{ Value, Checked }]
				SourceConfigByValue: {},
				Loaded: false,
				Result: null,               // { Succeeded, Failed, Details:[{Value, OK, Error?}] }
				Loading: false,
				Message: '',
			};
		}
		return this.pict.AppData.ConfigClone;
	}

	get state()
	{
		return this._ensureState();
	}

	/** Default the source/target ids from the live session once it's known (init runs before login). */
	_ensureSessionDefaults()
	{
		const tmpState = this.state;
		if (!(parseInt(tmpState.SourceID, 10) > 0))
		{
			tmpState.SourceID = (tmpState.SourceScope === 'User') ? this.cv.idUser : this.cv.idCustomer;
		}
		if (!(parseInt(tmpState.TargetID, 10) > 0))
		{
			tmpState.TargetID = (tmpState.TargetScope === 'User') ? this.cv.idUser : this.cv.idCustomer;
		}
	}

	/** Resolve a level id, falling back to the session when 0/blank (Global is always 0). */
	_idOrSession(pScope, pID)
	{
		if (pScope === 'Global') { return 0; }
		const tmpID = parseInt(pID, 10);
		if (tmpID > 0) { return tmpID; }
		return (pScope === 'User') ? this.cv.idUser : this.cv.idCustomer;
	}

	// --- field setters (from the view) ---

	setField(pField, pValue)
	{
		this.state[pField] = pValue;
		// Changing a scope invalidates a defaulted id so the new scope's session id can fill in.
		if (pField === 'SourceScope') { this.state.SourceID = 0; }
		if (pField === 'TargetScope') { this.state.TargetID = 0; }
	}

	toggleValue(pValue)
	{
		const tmpRow = this.state.Values.find((pRow) => pRow.Value === pValue);
		if (tmpRow) { tmpRow.Checked = !tmpRow.Checked; }
	}

	setAllChecked(pChecked)
	{
		this.state.Values.forEach((pRow) => { pRow.Checked = !!pChecked; });
	}

	selectedValues()
	{
		return this.state.Values.filter((pRow) => pRow.Checked).map((pRow) => pRow.Value);
	}

	buildPlan()
	{
		return libClone.planClone(this.state.SourceConfigByValue, this.selectedValues());
	}

	// --- load + apply ---

	/** Read the source namespace's effective config (all values) at the chosen source scope/context. */
	async loadSourceValuesAsync()
	{
		const tmpState = this.state;
		this._ensureSessionDefaults();
		tmpState.Loading = true;
		tmpState.Message = '';
		tmpState.Result = null;
		try
		{
			const tmpBody = await this.cv._postJSON('Configuration/Get',
				{
					LevelContext: tmpState.SourceScope,
					LevelContextID: this._idOrSession(tmpState.SourceScope, tmpState.SourceID),
					NamespaceKey: tmpState.NamespaceKey,
				});
			const tmpMap = (tmpBody && tmpBody.Document) || tmpBody || {};
			tmpState.SourceConfigByValue = {};
			Object.keys(tmpMap)
				.filter((pKey) => pKey !== 'Config' && pKey !== 'LevelControlRecords')
				.forEach((pKey) => { tmpState.SourceConfigByValue[pKey] = tmpMap[pKey]; });
			tmpState.Values = Object.keys(tmpState.SourceConfigByValue).map((pValue) => ({ Value: pValue, Checked: true }));
			tmpState.Loaded = true;
			if (!tmpState.Values.length)
			{
				tmpState.Message = 'No values found for this namespace at the source context.';
			}
		}
		catch (pError)
		{
			tmpState.Message = `Could not load source: ${pError.message}`;
		}
		tmpState.Loading = false;
	}

	/**
	 * Apply the plan: Put each selected value at the target scope/context. Aggregates per-value results.
	 * @return {Promise<object|null>} the result summary
	 */
	async applyAsync()
	{
		const tmpState = this.state;
		const tmpPlan = this.buildPlan();
		if (!tmpPlan.length)
		{
			tmpState.Message = 'Nothing selected to clone.';
			return null;
		}
		if (!this.cv.canWriteScope(tmpState.TargetScope))
		{
			tmpState.Message = `You don't have permission to write the ${this.cv._scopeLabel(tmpState.TargetScope)} scope.`;
			return null;
		}
		tmpState.Loading = true;
		tmpState.Message = '';
		const tmpTargetID = this._idOrSession(tmpState.TargetScope, tmpState.TargetID);
		const tmpDetails = [];
		for (let i = 0; i < tmpPlan.length; i++)
		{
			const tmpItem = tmpPlan[i];
			const tmpURL = `Configuration/Put/${tmpState.NamespaceKey}/${tmpItem.NamespaceValue}/${tmpState.TargetScope}/${tmpTargetID}`;
			try
			{
				/* eslint-disable no-await-in-loop */
				await this.cv._postJSON(tmpURL, { Config: tmpItem.Config });
				/* eslint-enable no-await-in-loop */
				tmpDetails.push({ Value: tmpItem.NamespaceValue, OK: true });
			}
			catch (pError)
			{
				tmpDetails.push({ Value: tmpItem.NamespaceValue, OK: false, Error: pError.message });
			}
		}
		tmpState.Result =
		{
			Succeeded: tmpDetails.filter((pDetail) => pDetail.OK).length,
			Failed: tmpDetails.filter((pDetail) => !pDetail.OK).length,
			Details: tmpDetails,
		};
		tmpState.Loading = false;
		return tmpState.Result;
	}

	// --- derive view state ---

	deriveRenderState()
	{
		const tmpState = this.state;
		this._ensureSessionDefaults();

		tmpState.NamespaceOptions = libCatalog.CONFIG_NAMESPACE_CATALOG.map((pEntry) => (
			{
				Value: pEntry.NamespaceKey,
				Label: pEntry.Label,
				SelectedAttr: (pEntry.NamespaceKey === tmpState.NamespaceKey) ? 'selected' : '',
			}));

		tmpState.SourceScopeOptions = SCOPES.map((pScope) => (
			{
				Value: pScope,
				Label: this.cv._scopeLabel(pScope),
				SelectedAttr: (pScope === tmpState.SourceScope) ? 'selected' : '',
				DisabledAttr: '',
			}));
		tmpState.TargetScopeOptions = SCOPES.map((pScope) => (
			{
				Value: pScope,
				Label: this.cv._scopeLabel(pScope),
				SelectedAttr: (pScope === tmpState.TargetScope) ? 'selected' : '',
				DisabledAttr: this.cv.canWriteScope(pScope) ? '' : 'disabled',
			}));

		tmpState.ValueRows = (tmpState.Values || []).map((pRow) => (
			{ Value: pRow.Value, CheckedAttr: pRow.Checked ? 'checked' : '' }));

		const tmpSelectedCount = this.selectedValues().length;
		const tmpCanWriteTarget = this.cv.canWriteScope(tmpState.TargetScope);
		tmpState.SelectedCount = tmpSelectedCount;
		tmpState.CanApply = tmpSelectedCount > 0 && tmpCanWriteTarget;
		tmpState.ApplyDisabledAttr = tmpState.CanApply ? '' : 'disabled';

		// conditional slots
		tmpState.LoadingSlot = tmpState.Loading ? [ {} ] : [];
		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message } ] : [];
		tmpState.ValuesSlot = (tmpState.Loaded && (tmpState.Values || []).length) ? [ {} ] : [];
		tmpState.NoTargetPermSlot = (!tmpCanWriteTarget) ? [ { Scope: this.cv._scopeLabel(tmpState.TargetScope) } ] : [];
		tmpState.ResultSlot = tmpState.Result ? [ tmpState.Result ] : [];
		tmpState.ResultRows = tmpState.Result ? tmpState.Result.Details.map((pDetail) => (
			{
				Value: pDetail.Value,
				StatusClass: pDetail.OK ? 'has-text-success' : 'has-text-danger',
				StatusText: pDetail.OK ? 'cloned' : ('failed: ' + (pDetail.Error || 'error')),
			})) : [];

		return tmpState;
	}
}

module.exports = ConfigCloneProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ConfigCloneProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 41,
};
